/**
 * Quién está dentro: nombre y correo de la propia cuenta.
 *
 * Existe porque la caja del pie del sidebar enseñaba `u-93cf92ee` — el username
 * que genera Cognito con el patrón `u-${id.slice(0,8)}`. A una persona eso no le
 * dice nada, y el propósito de esa caja es saber en qué cuenta estás ANTES de
 * hacer algo. En un equipo compartido de biblioteca esa duda acaba en «lo hice
 * desde la cuenta de otra».
 *
 * El nombre no está en el token: Cognito guarda el correo y los claims de
 * alcance, no la identidad (ver `backend/lambda/api/cognitoAdmin.ts`). Vive en la
 * base, se corrige desde el panel, y por eso se pide — un espejo en Cognito
 * quedaría viejo al primer cambio de nombre.
 */
import { apiFetch } from "./api";

export interface Identidad {
  firstName: string;
  lastName: string | null;
  email: string | null;
}

/**
 * `GET /yo`. Devuelve null ante CUALQUIER fallo, y quien llama sigue adelante.
 *
 * Es deliberado que no distinga los errores: esto es un rótulo. Si falla, se
 * enseña el respaldo (`etiquetaDeCuenta`) y ya. Bloquear el panel —o enseñar un
 * error— porque no se pudo leer un nombre sería cambiar un problema cosmético
 * por uno de acceso.
 */
export async function getYo(): Promise<Identidad | null> {
  try {
    const res = await apiFetch("/yo");
    if (!res.ok) return null;
    return (await res.json()) as Identidad;
  } catch {
    return null;
  }
}

/** Nombre completo, o null si no hay nada que enseñar. */
export function nombreCompleto(i: Identidad | null): string | null {
  if (!i) return null;
  const n = [i.firstName, i.lastName].filter((p) => (p ?? "").trim()).join(" ").trim();
  return n || null;
}

/**
 * Qué va en la caja, con su orden de respaldo.
 *
 * Se degrada al comportamiento de HOY en vez de quedarse en blanco: sin nombre
 * ni correo —el participante registrado por apartamento, que no tiene ninguno de
 * los dos a propósito— sigue saliendo el username, que al menos distingue una
 * sesión de otra.
 */
export function etiquetaDeCuenta(
  nombre: string | null,
  email: string | null,
  usuario: string
): { titulo: string; detalle: string } {
  const n = (nombre ?? "").trim();
  const e = (email ?? "").trim();
  return { titulo: n || "Modo seguro", detalle: e || usuario };
}
